import _ from "lodash";
import React, { useMemo, useEffect } from "react";
import classes from "./SubmissionViewer.scss";
import { Avatar, AvatarGroup, IconButton, Tooltip } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import classNames from "classnames";
import {
  ArrowLeftPaneOutlined,
  ArrowRightPaneOutlined,
  ChevronLeftOutlined,
  ChevronRightOutlined,
  LockOpenOutlined,
  LockOutlined,
} from "@toddle-edu/ds-icons";

import {
  getSubmissionStatusLabelDetails,
  isIEAssessment,
} from "Projects/modules/ProjectModules";
import { isSubmissionTypeIBDefined } from "Projects/modules/Utils";
import TheatreMode from "UIComponents/TheatreMode";
import {
  getCustomDefaultSubmissionItemResponse,
  showSubmissionItem,
} from "Projects/routes/DeadlinesV2/modules/Utils";
import { StudentAlertsWrapper } from "AppComponents/StudentAlerts";
import SubmissionItem from "./components/SubmissionItem";
import EmptyStateComponent from "./components/EmptyState";
import ACLStorage from "lib/aclService";
import ACLStore from "lib/aclStore";
import {
  CAN_MANAGE_IA_EA_PERMISSION,
  SUBMISSION_TYPE_CUSTOM,
} from "Projects/Constants/stringConstants";

// Constants for Google Docs Playback button positioning
// picked value based on scss styles and can be updated if there are any changes in scss
const GOOGLE_PLAYBACK_CONSTANTS = {
  RIGHT_CONTAINER_WIDTH: 556,
  TOGGLE_BUTTON_HEIGHT: 48,
  PLAYBACK_BUTTON_GAP: 8,
  PLAYBACK_BUTTON_OFFSET: 16,
};

const TEST_ID_PREFIX = "projects-submission-viewer";

const SubmissionViewer = props => {
  const {
    projectStudentDetails,
    projectDetailsWithTask,
    showEmptyState,
    presetSelectedAttachment: presetSelectedAttachmentDetails = null,
    onCreateAttachment,
    onDeleteAttachment,
    onUpdateAttachment,
    onUpdateInputField,
    disabled: submissionViewerDisabled,
    showCircularSwitcher,
    onCircularSwitcherClick,
    isCircularSwitcherDisabled = false,
    projectId,
    autoCheckSimilarityRef,
    projectGroupSubType,
    toggleRightContainerVisibility,
    isRightContainerVisible = true,
    testId,
  } = props;

  const isProjectGroupIEAssessment = isIEAssessment({
    projectGroupSubType,
  });

  const canManageIAEA = isProjectGroupIEAssessment
    ? ACLStorage.getPermissionValue(CAN_MANAGE_IA_EA_PERMISSION)
    : true;

  const disabled = submissionViewerDisabled || !canManageIAEA;

  const { t } = useI18n(["common", "project"]);

  const {
    latestSubmissionResponse,
    task: projectGroupTask,
  } = projectDetailsWithTask;

  const {
    isLocked: isSubmissionLocked,
    status: submissionStatus,
    itemResponses: projectSubmissionItemResponses,
    submission: projectSubmission,
  } = latestSubmissionResponse || {};
  const {
    items: projectGroupSubmissionItems,
    fieldUID: projectGroupSubmissionFieldUID,
  } = projectSubmission[0] || {};

  const projectGroupSubmissionFieldConfig = _.get(
    projectSubmission,
    "[0].fieldConfig",
    {}
  )?.[projectGroupSubmissionFieldUID];

  const { submission, isStudentSubmissionEnabled } = projectGroupTask;

  const { type: submissionType } = submission || {};
  const IsSubmissionTypeIBDefined = isSubmissionTypeIBDefined({
    submissionType,
  });
  const isCustomSubmissionType = submissionType === SUBMISSION_TYPE_CUSTOM;

  const { studentDetails } = projectStudentDetails;
  const isMultipleStudents = _.size(studentDetails) > 1;

  const {
    id: studentId,
    name: studentName,
    profileImage: studentProfileImage,
    alertTypes,
  } = _.first(studentDetails) || {};

  const attachmentArray = [];
  _.forEach(projectSubmissionItemResponses, itemResponse => {
    if (itemResponse?.attachments)
      attachmentArray.push(...itemResponse.attachments);
  });

  const presetSelectedAttachment = presetSelectedAttachmentDetails
    ? _.find(attachmentArray, {
        id: presetSelectedAttachmentDetails?.id,
      })
    : null;

  const [selectedAttachment, setSelectedAttachment] = React.useState(
    presetSelectedAttachmentDetails
      ? {
          attachments: [_.omit(presetSelectedAttachment, ["presetAttachment"])],
          attachmentId: _.get(presetSelectedAttachment, "id", ""),
          isPost: true,
          isAnnotationEnabled:
            presetSelectedAttachmentDetails?.isAnnotationEnabled,
        }
      : null
  );

  const attachmentsArrayStringified = JSON.stringify(attachmentArray);

  useEffect(() => {
    if (
      !selectedAttachment?.attachmentId ||
      !_.find(attachmentArray, {
        id: selectedAttachment?.attachmentId,
      })
    ) {
      const selectedAttachment = _.first(attachmentArray);

      const itemResponse = _.find(projectSubmissionItemResponses, response =>
        _.some(response.attachments, { id: selectedAttachment?.id })
      );
      const submissionItemId = _.get(itemResponse, "submissionItem.id");
      const submissionItem = _.find(projectGroupSubmissionItems, {
        id: submissionItemId,
      });
      const { fieldUID, fieldConfig } = submissionItem || {};

      const { config: { isAnnotationEnabled } = {} } = _.get(
        fieldConfig,
        fieldUID,
        {}
      );
      setSelectedAttachment({
        attachments: [_.omit(selectedAttachment, ["presetAttachment"])],
        attachmentId: _.get(selectedAttachment, "id", ""),
        isPost: true,
        isAnnotationEnabled,
      });
    }
  }, [attachmentsArrayStringified]);

  const onAttachmentClick = ({ attachmentId, isAnnotationEnabled }) => {
    const newSelectedAttachment = _.find(attachmentArray, { id: attachmentId });
    setSelectedAttachment({
      attachments: [_.omit(newSelectedAttachment, ["presetAttachment"])],
      attachmentId: _.get(newSelectedAttachment, "id", ""),
      isPost: true,
      isAnnotationEnabled,
    });
  };

  const BodyData = () => {
    if (IsSubmissionTypeIBDefined) {
      return (
        <div className={classes.submissionItemsContainer}>
          {_.map(projectSubmissionItemResponses, itemResponse => {
            const {
              submissionItem: { id: itemId },
            } = itemResponse;

            const submissionItemFieldConfig = _.find(
              projectGroupSubmissionItems,
              {
                id: itemId,
              }
            )?.fieldConfig;

            const {
              submissionItem: { fieldUID },
            } = itemResponse;

            const fieldDetails = submissionItemFieldConfig[fieldUID];
            const fieldConfig = fieldDetails?.config;
            const otherFieldDetails = {
              fieldType: fieldDetails?.type,
            };
            const {
              hasDynamicSubmissionItems,
              config: { childrenFieldUIds },
            } = projectGroupSubmissionFieldConfig;

            const showItem = showSubmissionItem({
              hasDynamicSubmissionItems,
              fieldUIDs: _.get(childrenFieldUIds, submissionType, []),
              itemFieldUID: fieldUID,
            });

            return showItem ? (
              <SubmissionItem
                submissionItem={itemResponse}
                onAttachmentClick={onAttachmentClick}
                activeAttachment={_.get(selectedAttachment, "attachmentId")}
                onDeleteAttachment={onDeleteAttachment}
                onCreateAttachment={onCreateAttachment}
                onUpdateAttachment={onUpdateAttachment}
                onUpdateInputField={onUpdateInputField}
                isSubmissionLocked={isSubmissionLocked}
                disabled={disabled}
                projectId={projectId}
                {...fieldConfig}
                {...otherFieldDetails}
                autoCheckSimilarityRef={autoCheckSimilarityRef}
                isCustomSubmissionType={isCustomSubmissionType}
                testId={`${TEST_ID_PREFIX}-item-${itemId}`}
              />
            ) : null;
          })}
        </div>
      );
    } else {
      const itemResponse = getCustomDefaultSubmissionItemResponse({
        items: projectSubmissionItemResponses,
      });

      const submissionItem = {
        ...itemResponse,
        attachments: !isStudentSubmissionEnabled ? [] : attachmentArray,
      };

      return (
        <SubmissionItem
          submissionItem={submissionItem}
          onAttachmentClick={onAttachmentClick}
          activeAttachment={_.get(selectedAttachment, "attachmentId")}
          onDeleteAttachment={onDeleteAttachment}
          onCreateAttachment={onCreateAttachment}
          onUpdateAttachment={onUpdateAttachment}
          onUpdateInputField={onUpdateInputField}
          showResponseText={true}
          isSubmissionLocked={isSubmissionLocked}
          disabled={disabled}
          dontShowAttachments={!isStudentSubmissionEnabled}
          projectId={projectId}
          autoCheckSimilarityRef={autoCheckSimilarityRef}
          showSimilarity={true}
          isCustomSubmissionType={isCustomSubmissionType}
          testId={`${TEST_ID_PREFIX}-custom-item`}
        />
      );
    }
  };

  const {
    textColor: statusTextColor,
    label: statusLabel,
    icon: statusIcon,
  } = getSubmissionStatusLabelDetails({
    status: submissionStatus,
  });

  const { statusLockIcon, statusLockLabel } = useMemo(() => {
    const statusLockIcon = isSubmissionLocked ? (
      <LockOutlined size="xxx-small" variant="subtle" />
    ) : (
      <LockOpenOutlined size="xxx-small" variant="subtle" />
    );
    const statusLockLabel = isSubmissionLocked
      ? t("project:submission_closed")
      : t("project:submission_open");

    return { statusLockIcon, statusLockLabel };
  }, [isSubmissionLocked]);

  const shouldShowGiveFeedbackButton = useMemo(() => {
    const res = !disabled;
    if (IsSubmissionTypeIBDefined) {
      return res && selectedAttachment?.isAnnotationEnabled;
    }
    return res;
  }, [
    IsSubmissionTypeIBDefined,
    selectedAttachment,
    isSubmissionLocked,
    disabled,
  ]);

  const rightContainerClass = classNames({
    [classes.rightContainer]: true,
    [classes.rightContainerCollapsed]: !isRightContainerVisible,
  });

  const rightContainerHeaderClasses = classNames({
    [classes.rightContainerHeader]: true,
    [classes.rightContainerHeaderHidden]: !isRightContainerVisible,
  });

  const rightContainerBodyClasses = classNames({
    [classes.rightContainerBody]: true,
    [classes.rightContainerBodyHidden]: !isRightContainerVisible,
  });

  const playbackButtonPositionStyle = useMemo(() => {
    const isFeatureFlagEnabled =
      ACLStore.can("FeatureFlag:EnableGoogleDocsPlayback") &&
      ACLStore.can("FeatureFlag:ProjectGooglePlaybackFixs");
    if (!isFeatureFlagEnabled) return undefined;

    if (isRightContainerVisible) {
      return {
        right:
          GOOGLE_PLAYBACK_CONSTANTS.RIGHT_CONTAINER_WIDTH +
          GOOGLE_PLAYBACK_CONSTANTS.PLAYBACK_BUTTON_OFFSET,
      };
    }
    return {
      right: GOOGLE_PLAYBACK_CONSTANTS.PLAYBACK_BUTTON_OFFSET,
      bottom:
        GOOGLE_PLAYBACK_CONSTANTS.PLAYBACK_BUTTON_OFFSET +
        GOOGLE_PLAYBACK_CONSTANTS.TOGGLE_BUTTON_HEIGHT +
        GOOGLE_PLAYBACK_CONSTANTS.PLAYBACK_BUTTON_GAP,
    };
  }, [isRightContainerVisible]);

  return (
    <div className={classes.bodyContainer}>
      <div className={classes.leftContainer}>
        {showEmptyState ? (
          <EmptyStateComponent
            submissionType={submissionType}
            isStudentSubmissionEnabled={isStudentSubmissionEnabled}
            studentName={studentName}
          />
        ) : selectedAttachment ? (
          <TheatreMode
            currentMediaData={selectedAttachment}
            setShowGiveFeedbackModal={true}
            shouldShowGiveFeedbackButton={shouldShowGiveFeedbackButton}
            playbackButtonPositionStyle={playbackButtonPositionStyle}
          />
        ) : null}
      </div>
      <div className={rightContainerClass}>
        <div className={classes.rightContainerToggle}>
          <IconButton
            variant="neutral"
            type="outlined"
            size="medium"
            icon={
              !isRightContainerVisible ? (
                <Tooltip tooltip={t("common:expand_side_panel")}>
                  <ArrowLeftPaneOutlined />
                </Tooltip>
              ) : (
                <Tooltip tooltip={t("common:collapse_side_panel")}>
                  <ArrowRightPaneOutlined />
                </Tooltip>
              )
            }
            onClick={toggleRightContainerVisibility}
            testId={`${testId}-toggle`}
          />
        </div>
        <div className={rightContainerHeaderClasses}>
          <div className={classes.studentWithStatusContainer}>
            {isMultipleStudents ? (
              <AvatarGroup dsVersion="2.0" shape="square" size="medium" max={2}>
                {_.map(studentDetails, student => {
                  const { id, name, profileImage } = student;
                  return (
                    <Avatar key={id} name={name} src={profileImage} id={id} />
                  );
                })}
              </AvatarGroup>
            ) : (
              <Avatar
                dsVersion="2.0"
                name={studentName}
                src={studentProfileImage}
                id={studentId}
                shape="square"
                size="medium"
              />
            )}
            <div className={classes.studentWithStatus}>
              <div
                className={classNames("text-label", classes.studentName)}
                dir="auto"
              >
                {isMultipleStudents ? (
                  _.join(_.map(studentDetails, "name"), ", ")
                ) : (
                  <StudentAlertsWrapper
                    studentId={studentId}
                    alertTypes={alertTypes}
                    dir="auto"
                  >
                    {studentName}
                  </StudentAlertsWrapper>
                )}
              </div>
              <div
                className={classNames("text-body-s", classes.statusesContainer)}
              >
                {isStudentSubmissionEnabled && (
                  <>
                    <div className={classes.statusContainer}>
                      {statusIcon}
                      <span style={{ color: statusTextColor }}>
                        {t(statusLabel)}
                      </span>
                    </div>
                    <div className={classes.dot}></div>
                    <div className={classes.statusContainer}>
                      {statusLockIcon}
                      <span className={classes.subtleText}>
                        {statusLockLabel}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {showCircularSwitcher && (
            <div className={classes.circularSwitcherContainer}>
              <Tooltip
                tooltip={t("common:previous_with_label_small", {
                  label: t("project:student"),
                })}
              >
                <IconButton
                  icon={<ChevronLeftOutlined />}
                  type="plain"
                  variant="neutral"
                  name={"prev-item"}
                  disabled={isCircularSwitcherDisabled}
                  onClick={e => {
                    onCircularSwitcherClick({ event: e, direction: "prev" });
                  }}
                  testId={`${testId}-prev`}
                />
              </Tooltip>
              <Tooltip
                tooltip={t("common:next_with_label_small", {
                  label: t("project:student"),
                })}
              >
                <IconButton
                  icon={<ChevronRightOutlined />}
                  type="plain"
                  variant="neutral"
                  name={"prev-item"}
                  disabled={isCircularSwitcherDisabled}
                  onClick={e => {
                    onCircularSwitcherClick({ event: e, direction: "next" });
                  }}
                  testId={`${testId}-next`}
                />
              </Tooltip>
            </div>
          )}
        </div>
        <div className={rightContainerBodyClasses}>
          <BodyData />
        </div>
      </div>
    </div>
  );
};

export default SubmissionViewer;
